import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { LANGUAGES } from "../../i18n/translations";
import { trackEvent } from "../../lib/analytics";

export default function LanguageSwitcher({ compact = false, dark = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const select = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code === "zh" ? "zh-CN" : code === "pt" ? "pt-PT" : code;
    const HREFLANG = { pt: "pt-PT", en: "en", de: "de", fr: "fr", es: "es", nl: "nl", it: "it", zh: "zh-CN" };
    trackEvent("language_change", { language: HREFLANG[code] || code, source: "header" });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher"
        aria-label="Select language"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
          dark ? "text-slate-200 hover:bg-white/10" : "text-brand-500 hover:bg-slate-100"
        }`}
      >
        <Globe size={14} strokeWidth={2} />
        <span>{current.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="lang-menu"
          className={`absolute ${compact ? "left-0" : "right-0"} mt-2 min-w-[180px] rounded-lg border border-slate-200 bg-white shadow-xl py-1 z-50 animate-fade-in`}
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => select(l.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left ${
                l.code === current.code ? "text-brand-500 font-semibold" : "text-slate-700"
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1">{l.name}</span>
              {l.code === current.code && <Check size={14} className="text-brand-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
